import HeadingSmall from '@/components/layout/HeadingSmall';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import SettingsLayout from '@/layouts/settings/layout';
import { cn } from '@/lib/utils';
import { Head } from '@inertiajs/react';
import { ExternalLink, Monitor, Smartphone } from 'lucide-react';
import { useMemo, useState } from 'react';
import { route } from 'ziggy-js';

interface EmailTemplate {
    key: string;
    group: string;
    source: string;
    template: string;
    preference: string | null;
    mail_only: boolean;
    subjects: Record<string, string>;
}

interface EmailTemplatesProps {
    templates: EmailTemplate[];
    locales: string[];
    systemLocale: string;
}

/** Width the preview frame is pinned to, so a template can be checked both ways. */
const VIEWPORTS = {
    desktop: { width: '100%', icon: Monitor },
    mobile: { width: '390px', icon: Smartphone },
} as const;

type Viewport = keyof typeof VIEWPORTS;

export default function EmailTemplates({ templates, locales, systemLocale }: EmailTemplatesProps) {
    const { t } = useTranslations();
    const [locale, setLocale] = useState(systemLocale);
    const [viewport, setViewport] = useState<Viewport>('desktop');
    const [selectedKey, setSelectedKey] = useState(templates[0]?.key ?? '');

    const selected = templates.find((template) => template.key === selectedKey) ?? templates[0];

    // Preserve the catalog order while collecting each group's templates together.
    const groups = useMemo(() => {
        const ordered: { group: string; items: EmailTemplate[] }[] = [];

        templates.forEach((template) => {
            const existing = ordered.find((entry) => entry.group === template.group);
            if (existing) {
                existing.items.push(template);
            } else {
                ordered.push({ group: template.group, items: [template] });
            }
        });

        return ordered;
    }, [templates]);

    const previewUrl = selected ? route('settings.email-templates.preview', { template: selected.key, locale }) : '';

    const shortClassName = (fqcn: string) => fqcn.split('\\').slice(-2).join('\\');

    return (
        <AppLayout>
            <Head title={t('email_templates.title')} />
            <SettingsLayout>
                <div className="space-y-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <HeadingSmall title={t('email_templates.title')} description={t('email_templates.description')} />

                        <Tabs value={locale} onValueChange={setLocale}>
                            <TabsList>
                                {locales.map((code) => (
                                    <TabsTrigger key={code} value={code}>
                                        {t(`email_templates.locale.${code}`)}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                    </div>

                    <p className="text-muted-foreground text-xs">
                        {t('email_templates.locale.system_hint').replace(':locale', t(`email_templates.locale.${systemLocale}`))}
                    </p>

                    <div className="grid gap-4 lg:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] lg:items-start">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm">{t('email_templates.count').replace(':count', String(templates.length))}</CardTitle>
                            </CardHeader>
                            <CardContent className="px-0 pb-2">
                                {/* Deliberately uncapped: a scroll box here hid two thirds
                                    of the catalog and read as "those emails don't exist". */}
                                <nav>
                                    {groups.map(({ group, items }) => (
                                        <div key={group}>
                                            <p className="text-muted-foreground px-4 pt-4 pb-1 text-[11px] font-semibold tracking-wider uppercase">
                                                {t(`email_templates.groups.${group}`)}
                                            </p>
                                            {items.map((template) => (
                                                <Button
                                                    key={template.key}
                                                    variant="ghost"
                                                    onClick={() => setSelectedKey(template.key)}
                                                    aria-current={template.key === selected?.key}
                                                    className={cn(
                                                        'h-auto w-full flex-col items-start gap-0.5 rounded-none border-l-2 border-transparent px-4 py-2 text-left whitespace-normal',
                                                        template.key === selected?.key && 'border-l-primary bg-accent',
                                                    )}
                                                >
                                                    <span className="text-sm font-medium">{t(`email_templates.templates.${template.key}.name`)}</span>
                                                    <span className="text-muted-foreground w-full truncate text-xs">{template.subjects[locale]}</span>
                                                </Button>
                                            ))}
                                        </div>
                                    ))}
                                </nav>
                            </CardContent>
                        </Card>

                        {/* Explanation and preview stay stacked in the right-hand
                            column, so picking a template from the list never moves
                            the thing you are looking at. */}
                        {selected ? (
                            <div className="space-y-4">
                                <Card>
                                    <CardHeader className="gap-2 pb-3">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge variant="secondary">{t(`email_templates.groups.${selected.group}`)}</Badge>
                                            <Badge variant="outline">
                                                {selected.mail_only
                                                    ? t('email_templates.channels.mail_only')
                                                    : t('email_templates.channels.mail_and_in_app')}
                                            </Badge>
                                        </div>
                                        <CardTitle className="text-base">{selected.subjects[locale]}</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-[7.5rem_minmax(0,1fr)]">
                                            <dt className="text-muted-foreground text-xs font-medium">{t('email_templates.fields.recipients')}</dt>
                                            <dd className="text-sm">{t(`email_templates.templates.${selected.key}.recipients`)}</dd>

                                            <dt className="text-muted-foreground text-xs font-medium">{t('email_templates.fields.trigger')}</dt>
                                            <dd className="text-sm">{t(`email_templates.templates.${selected.key}.trigger`)}</dd>

                                            <dt className="text-muted-foreground text-xs font-medium">{t('email_templates.fields.channels')}</dt>
                                            <dd className="text-sm">
                                                {selected.preference
                                                    ? t('email_templates.channels.preference').replace(':key', selected.preference)
                                                    : t('email_templates.channels.always')}
                                            </dd>

                                            <dt className="text-muted-foreground text-xs font-medium">{t('email_templates.fields.source')}</dt>
                                            <dd className="font-mono text-xs break-all">{shortClassName(selected.source)}</dd>

                                            <dt className="text-muted-foreground text-xs font-medium">{t('email_templates.fields.template')}</dt>
                                            <dd className="font-mono text-xs break-all">{selected.template}</dd>
                                        </dl>
                                    </CardContent>
                                </Card>

                                <Card className="overflow-hidden">
                                    <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-3">
                                        <div>
                                            <CardTitle className="text-sm">{t('email_templates.preview.heading')}</CardTitle>
                                            <p className="text-muted-foreground text-xs">{t('email_templates.preview.sample_notice')}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Tabs value={viewport} onValueChange={(value) => setViewport(value as Viewport)}>
                                                <TabsList>
                                                    {(Object.keys(VIEWPORTS) as Viewport[]).map((device) => {
                                                        const Icon = VIEWPORTS[device].icon;

                                                        return (
                                                            <TabsTrigger key={device} value={device}>
                                                                <Icon className="mr-1.5 h-3.5 w-3.5" />
                                                                {t(`email_templates.preview.${device}`)}
                                                            </TabsTrigger>
                                                        );
                                                    })}
                                                </TabsList>
                                            </Tabs>
                                            <Button variant="outline" size="sm" asChild>
                                                <a href={previewUrl} target="_blank" rel="noreferrer">
                                                    <ExternalLink className="mr-2 h-4 w-4" />
                                                    {t('email_templates.preview.open')}
                                                </a>
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="bg-muted/40 flex justify-center p-0">
                                        <iframe
                                            key={`${selected.key}-${locale}`}
                                            src={previewUrl}
                                            title={t(`email_templates.templates.${selected.key}.name`)}
                                            sandbox=""
                                            style={{ width: VIEWPORTS[viewport].width }}
                                            className="bg-background h-[75vh] min-h-[36rem] max-w-full border-0"
                                        />
                                    </CardContent>
                                </Card>
                            </div>
                        ) : (
                            <p className="text-muted-foreground text-sm">{t('email_templates.empty')}</p>
                        )}
                    </div>
                </div>
            </SettingsLayout>
        </AppLayout>
    );
}
